import { QueryClient } from "@tanstack/react-query"
import { ApiError, NetworkDisconnectedError } from "../errors"
import { EventEmitter } from "../events"
import type { IApiClient } from "./types"
import { VerificationType } from "./types"

// Re-export types
export * from "./types"

/**
 * The default configuration for the API client
 */
export const DEFAULT_API_CONFIG = {
  baseUrl: "",
  headers: {
    "Content-Type": "application/json",
  },
  defaultQueryOptions: {
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    cacheTime: 0,
  },
  reconnectInterval: 5000, // 5 seconds
}

/**
 * Type definitions for API client
 */
export interface ApiClientConfig {
  baseUrl: string
  headers?: Record<string, string>
  defaultQueryOptions?: Record<string, unknown>
  reconnectInterval?: number
}

/**
 * Event types for the API client
 */
export type ConnectionStatusChangeEvent = "connected" | "disconnected"

// Define the event map for the API client
export type ApiClientEventMap = {
  connectionStatusChange: (status: ConnectionStatusChangeEvent) => void
}

/**
 * API client for communicating with the backend
 */
export class ApiClient
  extends EventEmitter<ApiClientEventMap>
  implements IApiClient
{
  private baseUrl: string
  private headers: Record<string, string>
  private defaultQueryOptions: Record<string, unknown>
  private reconnectInterval: number
  private _token: string | null = null
  private _isConnected: boolean = true
  private _reconnectTimer: number | null = null
  public queryClient: QueryClient

  constructor(config: ApiClientConfig) {
    super()
    this.baseUrl = config.baseUrl
    this.headers = config.headers || DEFAULT_API_CONFIG.headers
    this.defaultQueryOptions =
      config.defaultQueryOptions || DEFAULT_API_CONFIG.defaultQueryOptions
    this.reconnectInterval =
      config.reconnectInterval || DEFAULT_API_CONFIG.reconnectInterval

    this.queryClient = new QueryClient({
      defaultOptions: {
        queries: { ...this.defaultQueryOptions },
      },
    })
  }

  /**
   * Check if client is connected to the network
   */
  get isConnected(): boolean {
    return this._isConnected
  }

  /**
   * Set auth token for API requests
   */
  setToken(token: string | null): void {
    this._token = token
  }

  /**
   * Helper method to create a full URL
   */
  private buildUrl(path: string): string {
    return `${this.baseUrl}${path}`
  }

  /**
   * Start checking for reconnection
   * @private
   */
  private startReconnectTimer(): void {
    if (this._reconnectTimer !== null) {
      return // Timer already running
    }

    this._reconnectTimer = window.setInterval(() => {
      this.checkConnection()
    }, this.reconnectInterval)
  }

  /**
   * Stop checking for reconnection
   * @private
   */
  private stopReconnectTimer(): void {
    if (this._reconnectTimer !== null) {
      clearInterval(this._reconnectTimer)
      this._reconnectTimer = null
    }
  }

  /**
   * Check server connection
   */
  async checkConnection(): Promise<boolean> {
    try {
      // Simple health check to the server
      const response = await fetch(`${this.baseUrl}/api/health`)
      const wasConnected = this._isConnected
      this._isConnected = response.ok

      // If connection state changed and is now connected
      if (!wasConnected && this._isConnected) {
        this.stopReconnectTimer()
        this.emit("connectionStatusChange", "connected")
      }

      return this._isConnected
    } catch (_error) {
      const wasConnected = this._isConnected
      this._isConnected = false

      // If connection state changed and is now disconnected
      if (wasConnected && !this._isConnected) {
        this.startReconnectTimer()
        this.emit("connectionStatusChange", "disconnected")
      }

      return false
    }
  }

  /**
   * Helper method to make a fetch request
   */
  private async fetchJson<T>(
    url: string,
    options: RequestInit = {},
  ): Promise<T> {
    // Create headers with authentication token if available
    const requestHeaders = { ...this.headers }

    // Add authorization if token is available
    if (this._token) {
      requestHeaders["Authorization"] = `Bearer ${this._token}`
    }

    // Prepare the final request options
    const requestOptions = {
      ...options,
      headers: {
        ...requestHeaders,
        ...((options.headers as Record<string, string>) || {}),
      },
    }

    try {
      const response = await fetch(url, requestOptions)
      const data = await response.json()

      // If request succeeds, we know we're connected
      if (!this._isConnected) {
        this._isConnected = true
        this.stopReconnectTimer()
        this.emit("connectionStatusChange", "connected")
      }

      if (!response.ok) {
        const message = data.error || "An unexpected error occurred"
        throw new ApiError(message, response.status)
      }

      return data as T
    } catch (error) {
      // Handle network errors
      if (
        error instanceof TypeError &&
        (error.message.includes("Network request failed") ||
          error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError"))
      ) {
        // Mark as disconnected and start reconnect timer
        if (this._isConnected) {
          this._isConnected = false
          this.startReconnectTimer()
          this.emit("connectionStatusChange", "disconnected")
        }

        throw new NetworkDisconnectedError()
      }

      // Rethrow the original error if it's not a network error
      throw error
    }
  }

  /**
   * Clean up any timers when the client is no longer needed
   */
  dispose(): void {
    this.stopReconnectTimer()
    this.clearListeners()
  }

  /**
   * Implementation of IApiClient.on method
   */
  on(event: string, listener: (...args: any[]) => void): this {
    if (event === "connectionStatusChange") {
      super.on("connectionStatusChange", listener as any)
    }
    return this
  }

  /**
   * Implementation of IApiClient.off method
   */
  off(event: string, listener: (...args: any[]) => void): this {
    if (event === "connectionStatusChange") {
      super.off("connectionStatusChange", listener as any)
    }
    return this
  }

  /**
   * Auth API endpoints
   */
  auth = {
    /**
     * Request email verification
     */
    requestVerification: async (email: string, type: VerificationType) => {
      return this.fetchJson<{ success: boolean; token: string }>(
        this.buildUrl("/api/auth/request-verification"),
        {
          method: "POST",
          body: JSON.stringify({ email, type }),
        },
      )
    },

    /**
     * Check if email is already registered
     */
    checkEmail: async (email: string) => {
      return this.fetchJson<{ exists: boolean }>(
        this.buildUrl("/api/auth/check-email"),
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
      )
    },

    /**
     * Verify email with code
     */
    verifyEmail: async (
      email: string,
      token: string,
      code: string,
      type: VerificationType,
    ) => {
      return this.fetchJson<{ success: boolean }>(
        this.buildUrl("/api/auth/verify-email"),
        {
          method: "POST",
          body: JSON.stringify({ email, token, code, type }),
        },
      )
    },

    /**
     * Reset account
     */
    resetAccount: async (
      email: string,
      verificationToken: string,
      code: string,
      authPublicKey: string,
    ) => {
      return this.fetchJson<{ success: boolean; token: string }>(
        this.buildUrl("/api/auth/reset-account"),
        {
          method: "POST",
          body: JSON.stringify({
            email,
            verificationToken,
            code,
            authPublicKey,
          }),
        },
      )
    },

    /**
     * Sign up with verified email
     */
    signup: async (
      email: string,
      verificationToken: string,
      code: string,
      authPublicKey: string,
    ) => {
      return this.fetchJson<{ success: boolean; token: string }>(
        this.buildUrl("/api/auth/signup"),
        {
          method: "POST",
          body: JSON.stringify({
            email,
            verificationToken,
            code,
            authPublicKey,
          }),
        },
      )
    },

    /**
     * Request a challenge for login
     */
    challenge: async (email: string) => {
      return this.fetchJson<{ success: boolean; challenge: string }>(
        this.buildUrl("/api/auth/challenge"),
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
      )
    },

    /**
     * Login with email and signature
     */
    login: async (email: string, signature: string) => {
      return this.fetchJson<{ success: boolean; token: string }>(
        this.buildUrl("/api/auth/login"),
        {
          method: "POST",
          body: JSON.stringify({ email, signature }),
        },
      )
    },
  }
}

/**
 * Create a new API client
 */
export const createApiClient = (config: ApiClientConfig): ApiClient => {
  return new ApiClient(config)
}
